import { IActivityItem, IFullList } from '@/common/service/api/Activity/Activity.domain';
import { ICategoriesItem } from '@/common/service/api/Categories/Categories.domain';
import { TFaqItem } from '@/common/service/api/FAQ/FAQ.domain';
import EmptyAttractionsPage from '@/entities/Attractions/ui/EmptyAttractionsPage/EmptyAttractionsPage';
import {
    IDestinationCategory,
    IDestinationList,
} from '@/entities/Destination/domain/Destination.domain';
import FAQ from '@/shared/FAQ/ui/FAQ';
import TopAttractions from '@/shared/TopAttractions/ui/TopAttractions';
import { TLandmarksShort } from '@/screens/LandmarkMainPage/domain/Landmarks.domain';
import { TopLandmarks } from '@/shared/TopLandmarks/ui/TopLandmarks';
import { AttractionsDescription } from './AttractionsDescription/AttractionsDescription';
import { IAttributes } from '@/common/app/constants/attributions';
import { IRecommendsItem } from '@/shared/FeaturedActivities/ui/featuredActivitiesPreview/FeaturedActivitiesPreview.types';
import { ActivitiesList } from './ActivitiesList';
import { AnswersFAQ } from '@/screens/ActivityPage/ui/Answers/AnswersFAQ';
import { attractionsUrl, buildRootUrl } from '@/common/app/utils/urlsUtils';
import { FeaturedCached } from '@/shared/FeaturedActivities/ui/FeaturedCached';

type Props = {
    isEmpty: boolean;
    featuredCategories?: ICategoriesItem[];
    currentDestination: IDestinationList;
    featuredActivitiesTitle: string;
    fullList: IFullList;
    faq?: TFaqItem[];
    title: string;
    listNameGA3?: string;
    isCategoryPage?: boolean;
    currentCategory?: IDestinationCategory;
    landmarks?: TLandmarksShort;
    categoryDescription?: string;
    availableAttributes: IAttributes[];
    recommendedTrips?: IRecommendsItem[];
    placeholders: {
        [key: string]: string;
    };
    firstItem?: IActivityItem;
    image: string;
};

export const AttractionsList = ({
    faq,
    isEmpty,
    currentDestination,
    currentCategory,
    title,
    fullList,
    featuredActivitiesTitle,
    featuredCategories,
    listNameGA3,
    isCategoryPage,
    landmarks,
    categoryDescription,
    availableAttributes,
    placeholders,
    recommendedTrips,
    firstItem,
    image,
}: Props) => {
    const item_list_id = currentCategory
        ? `${currentDestination.id}/${currentCategory.id}`
        : currentDestination.id;

    const item_list_name = currentCategory
        ? `${currentDestination.name}/${currentCategory.name}`
        : currentDestination.name;

    if (isEmpty) {
        return (
            <EmptyAttractionsPage
                redirectToDestination={`/destination/${currentDestination.state.code}/${currentDestination.slug}/things-to-do/`.toLowerCase()}
                isCategoryPage={isCategoryPage}
                destinationName={currentDestination.name}
            />
        );
    }

    const pageUrl = buildRootUrl(
        attractionsUrl({
            destination: currentDestination.slug,
            stateCode: currentDestination.state.code,
            category: currentCategory?.slug,
        })
    );

    return (
        <>
            <AttractionsDescription
                title={title}
                description={
                    isCategoryPage ? categoryDescription : fullList.destination_description
                }
                hideDescription={!!firstItem}
            />

            {Boolean(featuredCategories?.length) && (
                <TopAttractions
                    featuredCategories={featuredCategories ?? []}
                    destinationName={currentDestination.name}
                />
            )}
            <FeaturedCached
                defaultActivities={recommendedTrips || []}
                title={featuredActivitiesTitle}
                item_list_id={item_list_id}
                item_list_name={item_list_name}
                placeholders={placeholders}
                isPriority
            />
            <ActivitiesList
                title={title}
                defaultList={fullList?.items?.slice(0, 4)}
                listNameGA3={listNameGA3}
                listName={`${currentDestination.name}${
                    currentCategory ? '/' + currentCategory.name : ''
                }`}
                listId={`${currentDestination.id}${
                    currentCategory ? '/' + currentCategory.id : ''
                }`}
                availableAttributes={availableAttributes}
                item_list_id={item_list_id}
                item_list_name={item_list_name}
                placeholders={placeholders}
                recommends={recommendedTrips || []}
                firstItem={firstItem}
            />
            <AnswersFAQ
                title={title}
                url={pageUrl}
                image={image}
                id={
                    currentCategory?.id
                        ? `d${currentDestination.id}c${currentCategory?.id}`
                        : `d${currentDestination.id}`
                }
            />
            <FAQ data={faq}>
                <TopLandmarks
                    landmarks={landmarks}
                    destinationName={currentDestination.name}
                    destinationSlug={currentDestination.slug}
                    stateCode={currentDestination.state.code}
                />
            </FAQ>
            <FeaturedCached
                defaultActivities={recommendedTrips || []}
                title={featuredActivitiesTitle.replace('Featured', 'Sponsored')}
                item_list_id={item_list_id}
                item_list_name={item_list_name}
                placeholders={placeholders}
            />
        </>
    );
};
